import ApiService from '@/core/services/api.service';

const resource = 'v1/admin/shopee';
const reportsResource = `${resource}/reports`;

function compactBody(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined && v !== null),
  );
}

export default {
  async getStatus() {
    try {
      const response = await ApiService.get(`${resource}/status`);
      return response.data.data;
    } catch (error) {
      console.error('[ShopeeAdminRepository] Error fetching status:', error);
      throw error;
    }
  },

  async getOrderLogs(params = {}) {
    try {
      const response = await ApiService.get(`${resource}/orders/logs`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching order logs:',
        error,
      );
      throw error;
    }
  },

  /** Đếm đơn theo externalStatus — cùng bộ filter GET orders/logs (không truyền externalStatus để có đủ breakdown tab). */
  async getOrderLogsCountByStatus(params = {}) {
    try {
      const response = await ApiService.get(
        `${resource}/orders/logs/count-by-status`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching order logs count-by-status:',
        error,
      );
      throw error;
    }
  },

  /** Kênh logistics Shopee cho filter ĐVVC / kênh VC (BDropdownCustom). */
  async getOrderLogisticsOptions(params = {}) {
    console.log(
      '[ORDER_LOG_LOGISTICS_DEBUG] ShopeeAdminRepository.getOrderLogisticsOptions',
      JSON.stringify({ params, url: `${resource}/orders/logs/logistics-options` }),
    );
    try {
      const response = await ApiService.get(`${resource}/orders/logs/logistics-options`, {
        params,
      });
      console.log(
        '[ORDER_LOG_LOGISTICS_DEBUG] ShopeeAdminRepository.getOrderLogisticsOptions.response',
        JSON.stringify({
          status: response?.status,
          success: response?.data?.success,
          dataCount: Array.isArray(response?.data?.data) ? response.data.data.length : null,
        }),
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching logistics options:',
        error,
      );
      throw error;
    }
  },

  /**
   * Xuất Excel — cùng query filter với GET orders/logs (không phân trang theo page UI).
   * Response: blob stream + Content-Disposition.
   */
  async exportOrderLogs(params = {}) {
    try {
      return await ApiService.get(`${resource}/orders/logs/export`, {
        params,
        responseType: 'blob',
      });
    } catch (error) {
      console.error('[ShopeeAdminRepository] Error exporting order logs:', error);
      throw error;
    }
  },

  async getSyncQueue(params = {}) {
    try {
      const response = await ApiService.get(`${resource}/orders/sync-queue`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching sync queue:',
        error,
      );
      throw error;
    }
  },

  async getOrderReconciliation(params = {}) {
    try {
      const defaultParams = { limit: 50, ...params };
      const response = await ApiService.get(
        `${resource}/orders/reconciliation`,
        { params: defaultParams },
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching order reconciliation:',
        error,
      );
      throw error;
    }
  },

  /** Xuất Excel đối soát — cùng filter GET orders/reconciliation. */
  async exportOrderReconciliation(params = {}) {
    try {
      return await ApiService.get(`${resource}/orders/reconciliation/export`, {
        params,
        responseType: 'blob',
      });
    } catch (error) {
      console.error('[ShopeeAdminRepository] Error exporting reconciliation:', error);
      throw error;
    }
  },

  async getInventoryLogs(params = {}) {
    try {
      const response = await ApiService.get(`${resource}/inventory/logs`, {
        params,
      });
      return {
        items: response.data.data || [],
        pagination: response.data.pagination || {},
      };
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching inventory logs:',
        error,
      );
      throw error;
    }
  },

  async syncOneOrder({ orderSn, connectionId }) {
    try {
      const response = await ApiService.post(`${resource}/orders/sync-one`, {
        orderSn,
        connectionId,
      });
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error syncing one order:',
        error,
      );
      throw error;
    }
  },

  async retryOrder({ externalOrderId, connectionId }) {
    try {
      const response = await ApiService.post(`${resource}/orders/retry`, {
        externalOrderId,
        connectionId,
      });
      return response.data.data;
    } catch (error) {
      console.error('[ShopeeAdminRepository] Error retrying order:', error);
      throw error;
    }
  },

  /**
   * Ship kèm mã (non_integrated) hoặc pickup/dropoff theo tài liệu Shopee.
   */
  async shipWithTracking(payload) {
    try {
      const body = compactBody({
        orderSn: payload.orderSn,
        connectionId: payload.connectionId,
        trackingNumber: payload.trackingNumber,
        shipType: payload.shipType || 'non_integrated',
        packageNumber: payload.packageNumber ?? '0',
        pickup: payload.pickup ?? {},
        dropoff: payload.dropoff ?? {},
      });
      const response = await ApiService.post(
        `${resource}/orders/ship-with-tracking`,
        body,
      );
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error shipping with tracking:',
        error,
      );
      throw error;
    }
  },

  /**
   * POST ship-with-tracking/batch — giao hàng đồng loạt nhiều đơn.
   */
  async shipWithTrackingBatch(payload = {}) {
    try {
      const response = await ApiService.post(
        `${resource}/orders/ship-with-tracking/batch`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error shipping with tracking batch:',
        error,
      );
      throw error;
    }
  },

  /**
   * Raw Shopee get_shipping_parameter — FE parse dropdown (fe-shopee-shipping-auto-flow.md).
   */
  async fetchShippingParameter(params = {}) {
    try {
      const response = await ApiService.get(`${resource}/orders/shipping-parameter`, {
        params: compactBody(params),
      });
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetchShippingParameter:',
        error,
      );
      throw error;
    }
  },

  /**
   * Flow integrated: confirm_order → get_shipping_parameter → ship_order → get_tracking_number (backend).
   */
  async createShipment(payload) {
    try {
      const response = await ApiService.post(
        `${resource}/orders/create-shipment`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error creating shipment:',
        error,
      );
      throw error;
    }
  },

  /**
   * Chỉ ship_order (logistics.ship_order) — phù hợp READY_TO_SHIP / RETRY_SHIP khi không cần tracking ngay.
   * Body cùng schema với create-shipment (orderSn, connectionId, shipType, pickup, dropoff, …).
   */
  async confirmShipOrder(payload) {
    try {
      const response = await ApiService.post(
        `${resource}/orders/confirm-ship-order`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error confirm-ship-order:',
        error,
      );
      throw error;
    }
  },

  /**
   * Đồng bộ danh sách đơn (vd. 7 ngày) — POST sync-list.
   */
  async syncOrderList(payload = {}) {
    try {
      const response = await ApiService.post(
        `${resource}/orders/sync-list`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error sync-list orders:',
        error,
      );
      throw error;
    }
  },

  /**
   * GET print-tracking — JSON: trackingNumber, orderInfo, pdfBase64 (có thể null).
   * Tham số: orderSn + connectionId HOẶC orderId (+ packageNumber tùy chọn).
   */
  async fetchPrintTracking(params = {}) {
    try {
      const response = await ApiService.get(`${resource}/orders/print-tracking`, {
        params: compactBody(params),
      });
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching print tracking:',
        error,
      );
      throw error;
    }
  },

  /**
   * POST print-tracking — RFC: khuyến nghị khi gửi pickup/dropoff trong JSON body (không dùng query string dài).
   */
  async fetchPrintTrackingPost(payload = {}) {
    try {
      const response = await ApiService.post(
        `${resource}/orders/print-tracking`,
        compactBody(payload),
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching print tracking (POST):',
        error,
      );
      throw error;
    }
  },

  /**
   * GET print-tracking/pdf — thường trả pdfBase64 trong JSON khi flow Shopee thành công.
   */
  async fetchPrintTrackingPdf(params = {}) {
    try {
      const response = await ApiService.get(
        `${resource}/orders/print-tracking/pdf`,
        { params: compactBody(params) },
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching print tracking PDF:',
        error,
      );
      throw error;
    }
  },

  /**
   * POST print-tracking/pdf — RFC: body có shipType/pickup/dropoff khi logistics tích hợp cần ship tay.
   */
  async fetchPrintTrackingPdfPost(payload = {}) {
    try {
      const response = await ApiService.post(
        `${resource}/orders/print-tracking/pdf`,
        compactBody(payload),
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching print tracking PDF (POST):',
        error,
      );
      throw error;
    }
  },

  /**
   * POST print-tracking/pdf/batch — in nhiều vận đơn cùng lúc.
   */
  async fetchPrintTrackingPdfBatch(payload = {}) {
    try {
      const response = await ApiService.post(
        `${resource}/orders/print-tracking/pdf/batch`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching print tracking PDF batch:',
        error,
      );
      throw error;
    }
  },

  /** @deprecated Dùng fetchPrintTracking + utils/shopeePrintTracking */
  async getPrintTracking({ orderSn, connectionId, orderId, packageNumber }) {
    return this.fetchPrintTracking(
      compactBody({ orderSn, connectionId, orderId, packageNumber }),
    );
  },

  async syncStatusToShopee(payload) {
    try {
      const body = compactBody({
        orderId: payload.orderId,
        status: payload.status,
        trackingNumber: payload.trackingNumber,
        cancelReason: payload.cancelReason,
        shipType: payload.shipType,
        pickup: payload.pickup,
        dropoff: payload.dropoff,
        useOrderSnAsFallback:
          payload.useOrderSnAsFallback !== undefined
            ? payload.useOrderSnAsFallback
            : true,
      });
      const response = await ApiService.post(
        `${resource}/orders/sync-status-to-shopee`,
        body,
      );
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error syncing status to Shopee:',
        error,
      );
      throw error;
    }
  },

  async syncAllInventory(connectionId) {
    try {
      const response = await ApiService.post(`${resource}/inventory/sync-all`, {
        connectionId,
      });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  async triggerOrderSync(payload = {}) {
    try {
      const response = await ApiService.post(
        `${resource}/orders/sync`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error triggering order sync:',
        error,
      );
      throw error;
    }
  },

  // Complaint APIs
  async getComplaintReasons() {
    try {
      const response = await ApiService.get(
        `${resource}/orders/complaint/reasons`,
      );
      return response.data?.data || [];
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching complaint reasons:',
        error,
      );
      throw error;
    }
  },

  async getComplaintStatuses() {
    try {
      const response = await ApiService.get(
        `${resource}/orders/complaint/statuses`,
      );
      return response.data?.data || [];
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching complaint statuses:',
        error,
      );
      throw error;
    }
  },

  async createComplaint(payload) {
    try {
      const response = await ApiService.post(
        `${resource}/orders/complaint/create`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error creating complaint:',
        error,
      );
      throw error;
    }
  },

  async getComplaintList(params = {}) {
    try {
      const response = await ApiService.get(
        `${resource}/orders/complaint/list`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching complaint list:',
        error,
      );
      throw error;
    }
  },

  async getComplaintsByOrderNumbers(orderNumbers = []) {
    if (!orderNumbers.length) return [];
    const set = new Set(orderNumbers.map(String));
    try {
      const response = await ApiService.get(
        `${resource}/orders/complaint/list`,
        { params: { limit: 500, page: 1 } },
      );
      const raw = response.data?.data ?? response.data?.items ?? response.data;
      const items = Array.isArray(raw) ? raw : [];
      return items.filter(
        (c) =>
          set.has(String(c.orderNumber || '')) ||
          set.has(String(c.platformOrderId || '')),
      );
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching complaints by order numbers:',
        error,
      );
      return [];
    }
  },

  async updateComplaint(id, payload) {
    try {
      const response = await ApiService.patch(
        `${resource}/orders/complaint/${id}`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error updating complaint:',
        error,
      );
      throw error;
    }
  },

  async closeComplaint(id, payload = {}) {
    try {
      const response = await ApiService.patch(
        `${resource}/orders/complaint/${id}/close`,
        payload,
      );
      return response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error closing complaint:',
        error,
      );
      throw error;
    }
  },

  async getReturnDetail({ returnSn, connectionId }) {
    try {
      const response = await ApiService.get(`${resource}/returns/detail`, {
        params: { returnSn, connectionId },
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error fetching return detail:',
        error,
      );
      throw error;
    }
  },

  /** RTS pick-list — dateFrom/dateTo required (YYYY-MM-DD); connectionId optional. */
  async getReportPickList(params = {}) {
    try {
      const response = await ApiService.get(`${reportsResource}/pick-list`, {
        params: compactBody(params),
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error('[ShopeeAdminRepository] Error pick-list report:', error);
      throw error;
    }
  },

  /** Phễu đơn (counts + ratios); connectionId optional. */
  async getReportOrderFunnel(params = {}) {
    try {
      const response = await ApiService.get(`${reportsResource}/order-funnel`, {
        params: compactBody(params),
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error('[ShopeeAdminRepository] Error order-funnel report:', error);
      throw error;
    }
  },

  /** Theo kết nối marketplace — không gửi connectionId (BE tổng hợp). */
  async getReportByMarketplace(params = {}) {
    try {
      const response = await ApiService.get(`${reportsResource}/by-marketplace`, {
        params: compactBody(params),
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error by-marketplace report:',
        error,
      );
      throw error;
    }
  },

  /** Báo cáo đơn hàng (gộp phễu + marketplace). */
  async getReportOrderReport(params = {}) {
    try {
      const response = await ApiService.get(`${reportsResource}/order-report`, {
        params: compactBody(params),
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error('[ShopeeAdminRepository] Error order-report:', error);
      throw error;
    }
  },

  /** Báo cáo lãi lỗ — view: order | imei | product. */
  async getReportProfitLoss(params = {}) {
    try {
      const response = await ApiService.get(`${reportsResource}/profit-loss`, {
        params: compactBody(params),
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error('[ShopeeAdminRepository] Error profit-loss report:', error);
      throw error;
    }
  },

  /** Theo sản phẩm — paging; connectionId optional. */
  async getReportByProduct(params = {}) {
    try {
      const response = await ApiService.get(`${reportsResource}/by-product`, {
        params: compactBody(params),
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error('[ShopeeAdminRepository] Error by-product report:', error);
      throw error;
    }
  },

  /** Giải externalOrderIds theo cùng filter GET orders/logs (bulk / chọn đa trang). */
  async resolvePlatformOrderIds(payload = {}) {
    try {
      const response = await ApiService.post(
        `${reportsResource}/resolve-platform-order-ids`,
        compactBody(payload),
      );
      return response.data?.data ?? response.data;
    } catch (error) {
      console.error(
        '[ShopeeAdminRepository] Error resolve-platform-order-ids:',
        error,
      );
      throw error;
    }
  },
};
